import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useCreateAgentChatThreadMutation,
  useListAgentChatThreadsQuery,
} from '@/services/api/agentChatService';
import { AgentChatThread } from '@/services/types/agent-chat-types';

interface UseCoverageAgentThreadOptions {
  matterId?: string | null;
  coverageId?: string | null;
  title?: string;
}

interface UseCoverageAgentThreadResult {
  threadId: string | null;
  threads: AgentChatThread[];
  isLoading: boolean;
}

export const useCoverageAgentThread = ({
  matterId,
  coverageId,
  title = 'Coverage FinchGPT Review',
}: UseCoverageAgentThreadOptions): UseCoverageAgentThreadResult => {
  const enabled = Boolean(matterId && coverageId);
  const listArgs = enabled
    ? { matterId: matterId as string, coverageId: coverageId as string }
    : skipToken;

  const {
    data: threads = [],
    isLoading: isListLoading,
    isFetching,
  } = useListAgentChatThreadsQuery(listArgs);

  const [createThread, createState] = useCreateAgentChatThreadMutation();
  const hasRequestedThreadRef = useRef(false);
  const [isSeedingThread, setIsSeedingThread] = useState(false);

  useEffect(() => {
    if (!enabled) {
      hasRequestedThreadRef.current = false;
      setIsSeedingThread(false);
      return;
    }
    if (isListLoading || isFetching) return;
    if (threads.length > 0) {
      hasRequestedThreadRef.current = false;
      if (isSeedingThread) {
        setIsSeedingThread(false);
      }
      return;
    }
    if (createState.isLoading || hasRequestedThreadRef.current) {
      return;
    }
    hasRequestedThreadRef.current = true;
    setIsSeedingThread(true);
    createThread({
      matterId: matterId as string,
      data: {
        title,
        initial_task: null,
        workspace_id: null,
        coverage_id: coverageId as string,
      },
    })
      .unwrap()
      .catch(() => {
        hasRequestedThreadRef.current = false;
        setIsSeedingThread(false);
      });
  }, [
    enabled,
    isListLoading,
    isFetching,
    threads.length,
    createThread,
    createState.isLoading,
    matterId,
    coverageId,
    title,
    isSeedingThread,
  ]);

  const threadId = useMemo(() => {
    if (threads.length > 0) {
      return threads[0].id;
    }
    if (createState.data) {
      return createState.data.id;
    }
    return null;
  }, [threads, createState.data]);

  return {
    threadId,
    threads,
    isLoading:
      isListLoading || createState.isLoading || !enabled || isSeedingThread,
  };
};
